import React from 'react';
import { Button, FormGroup, Input, Label } from '../../../../components/Bootstrap';
import { Option, MenuOption } from '../../types';
import { NobleModal, Select } from '../../../../components';
import { useInputState } from '../../../../hooks';
import { AddingCustomFormGroup } from './AddingCustomFormGroup';
import { RequestFormGroup } from './RequestFormGroup';
import { OptionRow } from './OptionRow';

interface OptionSelectEditorProps {
  name: string;
  allowCustom: boolean;
  options: Option[];
  onCancel();
  onSubmit(options: MenuOption[]);
  onRequest(optionName: string);
}

type State = 'selecting' | 'adding-custom' | 'requesting';

type PriceFormGroupProps = {
  price: string;
  onChangePrice: React.ChangeEventHandler<HTMLInputElement>;
};

const PriceFormGroup: React.FC<PriceFormGroupProps> = ({ price, onChangePrice }) => {
  return (
    <FormGroup>
      <Label for='price'>Upgrade Price</Label>
      <Input type='number' value={price} onChange={onChangePrice} name='number' id='price' placeholder='$0' />
    </FormGroup>
  );
};

export const OptionSelectEditor: React.FC<OptionSelectEditorProps> = (props) => {
  const { options, onSubmit, onRequest } = props;

  const [requestName, setRequestName] = useInputState('');
  const [customName, setCustomName] = useInputState('');
  const [selected, setSelected] = React.useState([]);
  const [price, setPrice] = useInputState('0');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [state, setState] = React.useState<State>('selecting');

  const onSubmitWrapped = React.useCallback(
    (e) => {
      e.preventDefault();

      if (state === 'requesting') {
        onRequest(requestName);
      } else if (state === 'adding-custom') {
        onSubmit([
          {
            __typename: 'MenuOption' as 'MenuOption',
            id: null,
            price: Number(price),
            enabled: true,
            option: {
              __typename: 'Option' as 'Option',
              id: null,
              name: customName,
              imageUrl: null,
              posInfo: null,
              posPrice: null,
            },
          },
        ]);
      } else {
        onSubmit(
          selected.map((option) => ({
            __typename: 'MenuOption' as 'MenuOption',
            id: null,
            price: option.posPrice ?? Number(price),
            enabled: true,
            option,
          }))
        );
      }
    },
    [onSubmit, onRequest, state, price, customName, requestName, selected]
  );

  const handleSelect = React.useCallback(
    (option) => {
      if (selected.includes(option)) {
        setSelected(selected.filter((o) => o !== option));
      } else {
        setSelected(selected.concat([option]));
      }
    },
    [selected, setSelected]
  );

  const handleOpenDropdown = React.useCallback(() => {
    setDropdownOpen(true);
  }, []);

  const handleCloseDropdown = React.useCallback(() => {
    setDropdownOpen(false);
  }, [setDropdownOpen]);

  const handleAddCustom = React.useCallback(() => {
    setSelected([]);
    setDropdownOpen(false);
    setState('adding-custom');
  }, []);

  const handleRequestNewOption = React.useCallback(() => {
    setSelected([]);
    setDropdownOpen(false);
    setState('requesting');
  }, []);

  const getOffset = React.useCallback(
    (letter: string) => {
      const firstOption = options.find((option) => {
        return option.name[0].toLowerCase() === letter;
      });

      if (firstOption) {
        const index = options.indexOf(firstOption);
        const topMargin = state !== 'selecting' ? 84 : 55;
        return topMargin + index * 29;
      }
    },
    [options, state]
  );

  let selectValue = `Select ${props.name}`;

  if (state === 'adding-custom') {
    selectValue = `Add a ${props.name}`;
  } else if (state === 'requesting') {
    selectValue = `Request a ${props.name}`;
  } else if (selected.length !== 0) {
    selectValue = `${selected.length} selected`;
  }

  return (
    <NobleModal.Body>
      <div className='option-modal-content'>
        <FormGroup>
          <Label for='option'>{props.name}</Label>
          <Select
            open={dropdownOpen}
            onOpen={handleOpenDropdown}
            onClose={handleCloseDropdown}
            value={selectValue}
            getScrollOffsetForLetter={getOffset}
          >
            <Select.Row>Select {props.name}</Select.Row>
            {props.allowCustom && <Select.Row onClick={handleAddCustom}>{`Add a ${props.name}`}</Select.Row>}
            {!props.allowCustom && (
              <Select.Row onClick={handleRequestNewOption}>{`Request a ${props.name}`}</Select.Row>
            )}
            <Select.Separator />
            {options.map((option) => (
              <OptionRow
                key={option.id}
                option={option}
                isSelected={selected.includes(option)}
                onToggle={handleSelect}
              />
            ))}
          </Select>
        </FormGroup>
        {state === 'adding-custom' && (
          <AddingCustomFormGroup name={props.name} value={customName} onChange={setCustomName} />
        )}
        {state === 'requesting' && <RequestFormGroup name={props.name} value={requestName} onChange={setRequestName} />}
        {state !== 'requesting' && <PriceFormGroup price={price} onChangePrice={setPrice} />}
      </div>
      <div className='text-center mt-4'>
        <Button type='button' color='link' onClick={props.onCancel}>
          Cancel
        </Button>{' '}
        <Button color='primary' onClick={onSubmitWrapped}>
          Save
        </Button>
      </div>
    </NobleModal.Body>
  );
};
